/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, MapPin, Calendar, Hash, Factory, FileText, Film, Clock, CheckCircle, AlertTriangle, Wrench } from 'lucide-react';
import ClienteLayout from '../components/ClienteLayout';
import { prodottiAPI } from '../api/prodotti';
import { filesAPI } from '../api/files';
import { fmtData } from '../utils/date';
import { ctaManutenzione } from '../utils/manutenzione';
import { useViewport } from '../hooks/useViewport';

const ACCENT = '#3B82F6';
const UPLOADS_URL = process.env.REACT_APP_UPLOADS_URL || '';
const COLORI = { verde: '#10B981', arancione: '#F59E0B', rosso: '#EF4444', scaduta: '#DC2626' };
const COLORE_LABEL = { verde: 'In regola', arancione: 'In avvicinamento', rosso: 'Imminente', scaduta: 'Scaduta' };

export default function ProdottoDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isMobile } = useViewport();
  const { data: p, isLoading } = useQuery({ queryKey: ['prodotto', id], queryFn: () => prodottiAPI.detail(id) });
  const { data: categorie = [] } = useQuery({ queryKey: ['categorie-documento'], queryFn: () => filesAPI.categorieDocumento() });
  const etichettaDoc = (chiave) => categorie.find((c) => c.chiave === chiave)?.etichetta || 'Documento';

  if (isLoading) return <ClienteLayout><div style={styles.loading}>Caricamento...</div></ClienteLayout>;
  if (!p) return <ClienteLayout><div style={styles.loading}>Prodotto non trovato.</div></ClienteLayout>;

  return (
    <ClienteLayout>
      <button onClick={() => navigate('/prodotti')} style={styles.backBtn}><ArrowLeft size={16} /> I miei prodotti</button>

      {/* Header prodotto */}
      <div style={styles.headerCard}>
        <div style={{ ...styles.iconBox, backgroundColor: ACCENT + '15' }}><Package size={28} color={ACCENT} /></div>
        <div style={{ flex: 1 }}>
          <h1 style={styles.title}>{p.tipo_nome}</h1>
          <div style={styles.marca}>{[p.marca, p.modello].filter(Boolean).join(' ')}</div>
        </div>
      </div>

      {/* Dettagli tecnici */}
      <div style={styles.section}>
        <div style={styles.detailGrid}>
          {p.ubicazione && <Detail icon={MapPin} label="Ubicazione" value={p.ubicazione} />}
          {p.matricola && <Detail icon={Hash} label="Matricola" value={p.matricola} />}
          {p.anno_produzione && <Detail icon={Factory} label="Anno produzione" value={p.anno_produzione} />}
          {p.data_installazione && <Detail icon={Calendar} label="Installato il" value={fmtData(p.data_installazione)} />}
        </div>
        {p.descrizione && <p style={styles.descr}>{p.descrizione}</p>}
      </div>

      {/* Manutenzioni con barre grandi */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}><Wrench size={18} /> Stato manutenzioni</h3>
        <div style={styles.maintList}>
          {p.manutenzioni.map((m) => (
            <div key={m.manutenzione_tipo_id} style={styles.maintCard}>
              <div style={styles.maintHead}>
                <div>
                  <div style={styles.maintNome}>{m.nome}{m.opzionale ? <span style={styles.opzTag}>opzionale</span> : ''}</div>
                  <div style={styles.maintCadenza}>Ogni {m.cadenza_mesi} mesi</div>
                </div>
                <span style={{ ...styles.statoChip, backgroundColor: COLORI[m.colore] + '20', color: COLORI[m.colore] }}>
                  {m.colore === 'verde' ? <CheckCircle size={13} /> : <AlertTriangle size={13} />} {COLORE_LABEL[m.colore]}
                </span>
              </div>
              <div style={styles.barTrack}>
                <div style={{ ...styles.barFill, width: `${m.percentuale_rimanente}%`, backgroundColor: COLORI[m.colore] }} />
              </div>
              <div style={styles.maintFoot}>
                {m.colore === 'scaduta'
                  ? <span style={{ color: COLORI.scaduta, fontWeight: 600 }}>Scaduta da {Math.abs(m.giorni_rimanenti)} giorni</span>
                  : <span>Prossima entro il {fmtData(m.scadenza)} ({m.giorni_rimanenti} giorni)</span>}
              </div>
              {(m.colore === 'scaduta' || m.colore === 'rosso' || m.colore === 'arancione') && (() => {
                const cta = ctaManutenzione({
                  prodotto_id: p.id, prodotto: p.tipo_nome, marca: p.marca, modello: p.modello, ubicazione: p.ubicazione,
                  manutenzione: m.nome, manutenzione_tipo_id: m.manutenzione_tipo_id,
                  colore: m.colore, giorni_rimanenti: m.giorni_rimanenti,
                  intervento_stato: m.intervento_stato,
                });
                return (
                  <button
                    onClick={() => {
                      if (cta.vaiAIntervento) navigate(`/interventi/${cta.vaiAIntervento}`);
                      else navigate('/nuovo', { state: { precompilato: cta.precompilato } });
                    }}
                    style={{ ...styles.prenotaBtn, backgroundColor: cta.colore, ...(isMobile ? { width: '100%', padding: '0.7rem 1rem' } : {}) }}>
                    {cta.label}
                  </button>
                );
              })()}
            </div>
          ))}
        </div>
      </div>

      {/* Documenti / manuali */}
      {p.files && p.files.length > 0 && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}><FileText size={18} /> Documenti</h3>
          <div style={styles.filesGrid}>
            {p.files.map((f) => {
              const url = `${UPLOADS_URL}/${f.path ? f.path.replace('/uploads/', '') : f.nome_file}`;
              const isImg = f.mime_type && f.mime_type.startsWith('image/');
              const isVid = f.mime_type && f.mime_type.startsWith('video/');
              return (
                <a key={f.id} href={url} target="_blank" rel="noreferrer" style={styles.fileCard}>
                  {isImg ? <img src={url} alt="" style={styles.fileImg} />
                    : <div style={styles.fileIcon}>{isVid ? <Film size={26} color="#6B7280" /> : <FileText size={26} color="#6B7280" />}</div>}
                  <span style={styles.fileCat}>{etichettaDoc(f.categoria_documento)}</span>
                  <span style={styles.fileName}>{f.nome_originale}</span>
                </a>
              );
            })}
          </div>
        </div>
      )}

      {/* Storico interventi manutenzione */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}><Clock size={18} /> Storico manutenzioni</h3>
        {!p.storico || p.storico.length === 0 ? (
          <div style={styles.empty}>Nessuna manutenzione registrata finora.</div>
        ) : (
          <div style={styles.storicoList}>
            {p.storico.map((s) => (
              <div key={s.id} style={styles.storicoRow}>
                <div style={styles.storicoDot} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{s.manutenzione_nome}</div>
                  <div style={styles.storicoMeta}>
                    {fmtData(s.data_effettuata)}
                    {s.tecnico_nome && ` · ${s.tecnico_nome} ${s.tecnico_cognome}`}
                    {s.intervento_codice && ` · ${s.intervento_codice}`}
                  </div>
                  {s.note && <div style={styles.storicoNote}>{s.note}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </ClienteLayout>
  );
}

function Detail({ icon: Icon, label, value }) {
  return (
    <div style={styles.detailItem}>
      <Icon size={15} color="#9CA3AF" />
      <div>
        <div style={styles.detailLabel}>{label}</div>
        <div style={styles.detailValue}>{value}</div>
      </div>
    </div>
  );
}

const styles = {
  loading: { padding: '3rem', textAlign: 'center', color: '#6B7280' },
  backBtn: { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 0.9rem', backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', cursor: 'pointer', marginBottom: '1rem', fontSize: '0.88rem' },
  headerCard: { display: 'flex', alignItems: 'center', gap: '1rem', backgroundColor: 'white', padding: '1.25rem', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', marginBottom: '1rem' },
  iconBox: { width: '56px', height: '56px', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  title: { margin: 0, fontSize: '1.4rem', fontWeight: 700 },
  marca: { color: '#4B5563', fontSize: '0.95rem' },
  section: { backgroundColor: 'white', padding: '1.25rem', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', marginBottom: '1rem' },
  sectionTitle: { margin: '0 0 1rem', fontSize: '1.05rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.4rem' },
  detailGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' },
  detailItem: { display: 'flex', alignItems: 'flex-start', gap: '0.5rem' },
  detailLabel: { fontSize: '0.75rem', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.03em' },
  detailValue: { fontSize: '0.92rem', fontWeight: 600, color: '#374151' },
  descr: { marginTop: '1rem', marginBottom: 0, color: '#4B5563', fontSize: '0.9rem' },
  maintList: { display: 'flex', flexDirection: 'column', gap: '1rem' },
  maintCard: { padding: '1rem', backgroundColor: '#F9FAFB', borderRadius: '10px' },
  maintHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.65rem', gap: '0.5rem' },
  maintNome: { fontWeight: 700, fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' },
  opzTag: { fontSize: '0.68rem', backgroundColor: '#E5E7EB', color: '#6B7280', padding: '0.1rem 0.4rem', borderRadius: '4px', fontWeight: 600 },
  maintCadenza: { fontSize: '0.8rem', color: '#9CA3AF', marginTop: '0.15rem' },
  statoChip: { display: 'inline-flex', alignItems: 'center', gap: '0.25rem', padding: '0.25rem 0.6rem', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 700, whiteSpace: 'nowrap' },
  barTrack: { height: '12px', backgroundColor: '#E5E7EB', borderRadius: '6px', overflow: 'hidden' },
  barFill: { height: '100%', borderRadius: '6px', transition: 'width 0.4s' },
  maintFoot: { fontSize: '0.82rem', color: '#6B7280', marginTop: '0.5rem' },
  prenotaBtn: { marginTop: '0.75rem', padding: '0.5rem 1rem', backgroundColor: ACCENT, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' },
  filesGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: '0.75rem' },
  fileCard: { display: 'flex', flexDirection: 'column', gap: '0.4rem', textDecoration: 'none', color: '#374151' },
  fileImg: { width: '100%', height: '90px', objectFit: 'cover', borderRadius: '8px', display: 'block' },
  fileIcon: { height: '90px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F3F4F6', borderRadius: '8px' },
  fileCat: { fontSize: '0.78rem', fontWeight: 700, color: '#374151', lineHeight: 1.25 },
  fileName: { fontSize: '0.72rem', color: '#9CA3AF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  empty: { padding: '1.5rem', textAlign: 'center', color: '#9CA3AF', fontSize: '0.88rem' },
  storicoList: { display: 'flex', flexDirection: 'column', gap: '0.85rem' },
  storicoRow: { display: 'flex', gap: '0.75rem' },
  storicoDot: { width: '10px', height: '10px', borderRadius: '50%', backgroundColor: ACCENT, marginTop: '0.35rem', flexShrink: 0 },
  storicoMeta: { fontSize: '0.82rem', color: '#6B7280', marginTop: '0.15rem' },
  storicoNote: { fontSize: '0.85rem', color: '#4B5563', marginTop: '0.25rem', fontStyle: 'italic' },
};
